/**
 * BundleOptions Component
 * Manages bundle quantity selection with dynamic pricing
 * @version 2.1.0
 */

class BundleOptions {
  static DEBUG = false;
  static instances = new Map();

  constructor(blockId, sectionId) {
    this.log('info', `Initializing for block: ${blockId}`);
    
    const container = document.querySelector(`[data-block-id="${blockId}"]`);
    
    // Prevent duplicate initialization
    if (container?.dataset.bundleInitialized === 'true') {
      this.log('warn', 'Already initialized - skipping');
      return;
    }
    
    // Destroy existing instance if present
    if (BundleOptions.instances.has(blockId)) {
      this.log('info', 'Destroying existing instance');
      BundleOptions.instances.get(blockId).destroy();
      BundleOptions.instances.delete(blockId);
    }
    
    this.blockId = blockId;
    this.sectionId = sectionId;
    this.container = container;
    
    if (!this.container) {
      this.log('error', `Container not found: ${blockId}`);
      return;
    }
    
    this.container.dataset.bundleInitialized = 'true';
    this.quantityInput = document.querySelector(`#BundleQuantity-${sectionId}`);
    
    if (!this.quantityInput) {
      this.log('error', `Quantity input not found: #BundleQuantity-${sectionId}`);
      return;
    }
    
    this.boundHandler = null;
    
    this.init();
    BundleOptions.instances.set(blockId, this);
    this.log('success', 'Instance initialized');
  }
  
  log(level, ...args) {
    if (!BundleOptions.DEBUG) return;
    
    const colors = {
      info: 'color: blue',
      warn: 'color: orange',
      error: 'color: red',
      success: 'color: green; font-weight: bold'
    };
    
    const style = colors[level] || '';
    const prefix = '[BundleOptions]';
    
    if (style && args.length > 0 && typeof args[0] === 'string') {
      console.log(`%c${prefix} ${args[0]}`, style, ...args.slice(1));
    } else {
      console.log(prefix, ...args);
    }
  }

  init() {
    const checkedOption = this.container.querySelector('input[type="radio"]:checked');
    if (checkedOption) {
      const quantity = parseInt(checkedOption.dataset.quantity, 10) || 1;
      this.updateQuantity(quantity);
      this.updatePriceDisplay(checkedOption);
      this.log('info', `Initial state - Quantity: ${quantity}`);
    }
    
    this.boundHandler = (e) => this.handleOptionChange(e);
    this.container.addEventListener('change', this.boundHandler);
    this.log('info', 'Event listeners attached');
  }

  handleOptionChange(event) {
    if (event.target.type !== 'radio') return;
    
    const radio = event.target;
    const quantity = parseInt(radio.dataset.quantity, 10);
    
    this.log('info', `Option changed - Quantity: ${quantity}`);
    
    if (!isNaN(quantity) && quantity > 0) {
      this.updateQuantity(quantity);
      this.updatePriceDisplay(radio);
    }
  }

  updateQuantity(quantity) {
    this.quantityInput.value = quantity;
    
    const displayElements = document.querySelectorAll('[data-quantity-display]');
    displayElements.forEach(el => {
      el.textContent = quantity;
    });
    
    this.log('info', `Quantity: ${quantity}`);
  }

  updatePriceDisplay(radio) {
    const bundleOption = radio.closest('.bundle-option');
    if (!bundleOption) return;
    
    const comparePrice = bundleOption.querySelector('.bundle-option__compare-price');
    const regularPrice = bundleOption.querySelector('.bundle-option__price');
    if (!regularPrice) return;

    const comparePriceText = comparePrice?.textContent?.trim() || '';
    const regularPriceText = regularPrice.textContent.trim();
    const isOnSale = comparePriceText !== '';

    // Update .price block (standard Shopify structure)
    const priceBlock = document.querySelector('.price');
    if (priceBlock) {
      priceBlock.classList.toggle('price--on-sale', isOnSale);
      
      if (isOnSale) {
        // When on sale: sale price shows current price, regular shows compare price
        const saleEl = priceBlock.querySelector('.price__sale .price-item--sale');
        if (saleEl) {
          saleEl.textContent = regularPriceText;
        }
        
        const compareEl = priceBlock.querySelector('.price__regular .price-item--regular');
        if (compareEl) {
          compareEl.textContent = comparePriceText;
        }
      } else {
        // When not on sale: just show regular price
        const regularEl = priceBlock.querySelector('.price__regular > .price-item--regular');
        if (regularEl) {
          regularEl.textContent = regularPriceText;
        }
      }
    }
    
    // Update .f-price block (Minimog theme structure)
    const fPriceBlock = document.querySelector('.f-price');
    if (fPriceBlock) {
      fPriceBlock.classList.toggle('f-price--on-sale', isOnSale);
      
      if (isOnSale) {
        // Update sale price (the actual discounted price)
        const salePrice = fPriceBlock.querySelector('.f-price__sale > .f-price-item--sale');
        if (salePrice) {
          salePrice.textContent = regularPriceText;
        }
        
        // Update both compare prices (one in regular div, one strikethrough in sale div)
        const regularComparePrice = fPriceBlock.querySelector('.f-price__regular > .f-price-item--regular');
        if (regularComparePrice) {
          regularComparePrice.textContent = comparePriceText;
        }
        
        const saleComparePrice = fPriceBlock.querySelector('.f-price__sale .f-price-item--regular');
        if (saleComparePrice) {
          saleComparePrice.textContent = comparePriceText;
        }
      } else {
        // When not on sale: only update the regular price in .f-price__regular
        const regularPrice = fPriceBlock.querySelector('.f-price__regular > .f-price-item--regular');
        if (regularPrice) {
          regularPrice.textContent = regularPriceText;
        }
      }
    }
    
    if (isOnSale) {
      this.updateSaleBadge(comparePriceText, regularPriceText);
    } else {
      this.hideSaleBadge();
    }
    
    this.log('info', 'Price updated');
  }

  updateSaleBadge(comparePriceText, regularPriceText) {
    const compareValue = this.extractPriceValue(comparePriceText);
    const regularValue = this.extractPriceValue(regularPriceText);
    
    if (compareValue <= regularValue) return;
    
    const discountPercent = Math.round(((compareValue - regularValue) / compareValue) * 100);
    
    // Update standard .price badge
    const priceBadge = document.querySelector('.badge.price__badge-sale');
    if (priceBadge) {
      const badgeText = priceBadge.dataset.badgeText || 'Sale';
      priceBadge.textContent = badgeText.replace(/\d+/, discountPercent);
      priceBadge.style.display = '';
    }
    
    // Update .f-price badge (Minimog structure)
    const fPriceBadge = document.querySelector('.f-price__badge-sale [data-saved-price]');
    if (fPriceBadge) {
      fPriceBadge.textContent = discountPercent + '%';
    }
  }

  hideSaleBadge() {
    // Hide standard .price badge
    const priceBadge = document.querySelector('.badge.price__badge-sale');
    if (priceBadge) {
      priceBadge.style.display = 'none';
    }
    
    // Hide .f-price badge (Minimog structure)
    const fPriceBadgeContainer = document.querySelector('.f-price__badge-sale');
    if (fPriceBadgeContainer) {
      fPriceBadgeContainer.style.display = 'none';
    }
  }

  extractPriceValue(priceText) {
    if (!priceText) return 0;
    
    let cleaned = priceText.replace(/[€$£¥₹\s]/g, '');
    
    if (/(\d+),(\d+)/.test(cleaned) && !cleaned.includes('.')) {
      cleaned = cleaned.replace(',', '.');
    }
    
    cleaned = cleaned.replace(/[^\d.]/g, '');
    return parseFloat(cleaned) || 0;
  }

  destroy() {
    this.log('info', 'Destroying instance');
    
    if (this.container && this.boundHandler) {
      this.container.removeEventListener('change', this.boundHandler);
      delete this.container.dataset.bundleInitialized;
    }
    
    this.container = null;
    this.quantityInput = null;
    this.boundHandler = null;
    
    BundleOptions.instances.delete(this.blockId);
    this.log('info', 'Destroyed');
  }
}

if (typeof window !== 'undefined') {
window.BundleOptions = BundleOptions;
}
